/** API-derived dashboard metric group with explicit loading/unavailable states. */
import {
	Box,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FolderIcon from "@mui/icons-material/Folder";
import OpacityIcon from "@mui/icons-material/Opacity";
import ScienceIcon from "@mui/icons-material/Science";
import type { ReactNode } from "react";

import { GlassSurface } from "../../../layouts/components/GlassSurface";

export interface DashboardMetricValues {
	projects: number | null;
	samples: number | null;
	activeProjects: number | null;
	archivedProjects: number | null;
}

export interface DashboardMetricCardsProps {
	values: DashboardMetricValues;
	loading: boolean;
	unavailable: boolean;
}

interface MetricDefinition {
	key: keyof DashboardMetricValues;
	label: string;
	detail: string;
	icon: ReactNode;
	accent: "workspace.green" | "workspace.blue" | "workspace.bronze" | "warning.main";
}

const METRICS: MetricDefinition[] = [
	{
		key: "projects",
		label: "Projects",
		detail: "All projects returned for the active organization",
		icon: <FolderIcon />,
		accent: "workspace.green",
	},
	{
		key: "samples",
		label: "Soil samples",
		detail: "Sum of API-supplied sample counts",
		icon: <OpacityIcon />,
		accent: "workspace.blue",
	},
	{
		key: "activeProjects",
		label: "Active projects",
		detail: "Projects with ACTIVE status",
		icon: <ScienceIcon />,
		accent: "workspace.green",
	},
	{
		key: "archivedProjects",
		label: "Archived projects",
		detail: "Projects retained for reference",
		icon: <AssessmentIcon />,
		accent: "workspace.bronze",
	},
];

export function DashboardMetricCards({
	values,
	loading,
	unavailable,
}: DashboardMetricCardsProps) {
	return (
		<Box
			role="group"
			aria-labelledby="dashboard-metrics-title"
			aria-busy={loading}
			sx={{
				display: "grid",
				gridTemplateColumns: {
					xs: "minmax(0, 1fr)",
					sm: "repeat(2, minmax(0, 1fr))",
					xl: "repeat(4, minmax(0, 1fr))",
				},
				gap: 2,
			}}
		>
			{METRICS.map((metric) => (
				<GlassSurface
					component="article"
					key={metric.key}
					intensity="standard"
					depth="raised"
					aria-label={metric.label}
					sx={{
						minWidth: 0,
						height: "100%",
						p: 2.5,
						borderTop: "3px solid",
						borderTopColor: metric.accent,
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="flex-start">
						<Box
							aria-hidden="true"
							sx={{
								display: "grid",
								placeItems: "center",
								width: 42,
								height: 42,
								flexShrink: 0,
								borderRadius: 1.5,
								color: metric.accent,
								backgroundColor: "rgba(246,240,227,0.06)",
							}}
						>
							{metric.icon}
						</Box>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								component="h3"
								variant="overline"
								sx={{ color: "workspace.textSecondary", letterSpacing: "0.08em" }}
							>
								{metric.label}
							</Typography>
							{loading ? (
								<Skeleton
									aria-label={`Loading ${metric.label.toLowerCase()}`}
									variant="text"
									width={72}
									sx={{ bgcolor: "rgba(246,240,227,0.12)", fontSize: 40 }}
								/>
							) : (
								<Typography
									variant="h3"
									data-testid={`metric-${metric.key}`}
									sx={{ mt: 0.25, lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}
								>
									{unavailable ? "—" : values[metric.key]}
								</Typography>
							)}
							<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
								{unavailable ? "Unavailable from the project request" : metric.detail}
							</Typography>
						</Box>
					</Stack>
				</GlassSurface>
			))}
		</Box>
	);
}
